
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { ChartContainer, ChartTooltipContent } from '@/components/ui/chart';
import { useIsMobile } from '@/hooks/use-mobile';
import { type DateRangeType } from './DateRangeFilter';
import { Loader2 } from 'lucide-react';

interface DashboardData {
  name: string;
  visitors?: number;
  signups?: number;
  revenue?: number;
}

interface DashboardChartsProps {
  visitors: number;
  signups: number;
  revenue: number;
  dateRange: DateRangeType;
  timeSeriesData?: {
    visitors?: DashboardData[];
    courses?: DashboardData[];
    revenue?: DashboardData[];
  };
  isLoading?: boolean;
}

export function DashboardCharts({ 
  visitors, 
  signups, 
  revenue, 
  dateRange,
  timeSeriesData,
  isLoading = false
}: DashboardChartsProps) {
  const isMobile = useIsMobile();
  
  // Calculate the interval based on date range and screen size
  const getXAxisInterval = (dataLength: number) => {
    const dateRangeStr = typeof dateRange === 'string' ? dateRange : '7days';
    
    if (isMobile) {
      if (dateRangeStr === 'today') return 5;
      if (dateRangeStr === '7days') return 1;
      return Math.ceil(dataLength / 4);
    } else {
      if (dateRangeStr === 'today') return 3;
      if (dateRangeStr === '7days') return 1;
      return Math.ceil(dataLength / 8);
    }
  };

  const chartConfig = {
    visitors: {
      label: "Visitors",
      color: "#00dd00",
    },
    signups: {
      label: "Course Signups",
      color: "#00dd00",
    },
    revenue: {
      label: "Revenue",
      color: "#00dd00",
    }
  };

  const renderChart = (dataKey: 'visitors' | 'signups' | 'revenue', title: string, className?: string) => {
    // Select the appropriate data based on the chart type
    let data: DashboardData[] = [];
    
    if (timeSeriesData) {
      if (dataKey === 'visitors' && timeSeriesData.visitors && timeSeriesData.visitors.length > 0) {
        data = timeSeriesData.visitors;
      } else if (dataKey === 'signups' && timeSeriesData.courses && timeSeriesData.courses.length > 0) {
        data = timeSeriesData.courses;
      } else if (dataKey === 'revenue' && timeSeriesData.revenue && timeSeriesData.revenue.length > 0) {
        data = timeSeriesData.revenue;
      }
    }
    
    if (isLoading) {
      return (
        <div className={`bg-card p-4 rounded-lg shadow-md h-full ${className} flex items-center justify-center`}>
          <Loader2 className="h-8 w-8 animate-spin text-green-600" />
        </div>
      );
    }
    
    // Empty state when no data is available
    if (data.length === 0) {
      return (
        <div className={`bg-card p-4 rounded-lg shadow-md h-full ${className} flex flex-col items-center justify-center`}>
          <h3 className="text-lg font-semibold mb-2">{title}</h3>
          <p className="text-gray-500 text-center">No data available for this time period</p>
        </div>
      );
    }
    
    return (
      <div className={`bg-card p-4 rounded-lg shadow-md h-full ${className}`}>
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <div className="w-full h-[calc(100%-2rem)] min-h-[150px]">
          <ChartContainer config={chartConfig} className="h-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
                <defs>
                  <linearGradient id={`gradient-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00dd00" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#00dd00" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                <XAxis 
                  dataKey="name" 
                  stroke="currentColor" 
                  opacity={0.5}
                  tickLine={false}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  interval={getXAxisInterval(data.length)}
                />
                <YAxis 
                  stroke="currentColor" 
                  opacity={0.5} 
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                  width={isMobile ? 25 : 35}
                  tickFormatter={(value) => {
                    if (value >= 1000) {
                      return `${(value / 1000).toFixed(0)}k`;
                    }
                    return value;
                  }}
                />
                <Tooltip content={<ChartTooltipContent />} />
                <Area
                  type="monotone"
                  dataKey={dataKey}
                  stroke="#00dd00"
                  fill={`url(#gradient-${dataKey})`}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  isAnimationActive={true}
                  animationDuration={1500}
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      {renderChart("visitors", "Visitors Over Time", "w-full h-[250px] md:h-[300px]")}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderChart("signups", "Course Signups Over Time", "h-[200px] md:h-[250px]")}
        {renderChart("revenue", "Revenue Over Time", "h-[200px] md:h-[250px]")}
      </div>
    </div>
  );
}
